import { Component } from 'react';
import { reportClientError } from '../lib/errorReporting';

// Without this, a render-time error anywhere in the tree makes React quietly
// unmount everything — the user is left staring at the body's plain dark
// background (#020617 from index.css) with nothing on it, which is exactly
// the "pantalla oscura sin nada visible" reported on a Redmi Note 12 at the
// moment of selecting a transaction category. That device is too capable
// for this to be the GPU/blur renderer crash fixed elsewhere (see
// moneytrack/memory/gpu-blur-crash-policy.md) — this is a plain JS
// exception with no error boundary to catch it, so there was no way to see
// what actually threw. This makes the error visible on-screen and keeps a
// copy in localStorage (key: mipisto_last_crash) so it survives the reload
// the "Reintentar" button triggers.
export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportClientError({
      kind: 'render',
      message: error?.message,
      stack: `${error?.stack || ''}\n${info?.componentStack || ''}`,
    });
    persistCrash({
      type: 'render',
      message: error?.message,
      stack: error?.stack,
      componentStack: info?.componentStack,
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            minHeight: '100vh',
            background: 'var(--bg)',
            color: 'var(--text)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            fontFamily: 'Inter, sans-serif',
            textAlign: 'center',
          }}
          data-testid="error-boundary-fallback"
        >
          {/* The details already went to /admin → Logs (reportClientError);
              the person only needs to know what to do next. */}
          <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Algo salió mal</p>
          <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 4, maxWidth: 300 }}>
            Ya nos avisó la app. Recarga para seguir donde estabas.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20,
              background: 'var(--brand)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              padding: '10px 20px',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Recargar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Shared by the boundary above (render-phase errors) and the global
// listeners in index.js (errors thrown outside React's event delegation,
// e.g. in a rejected promise or a third-party library's async callback —
// those don't trip an error boundary but are worth capturing too).
export function persistCrash(details) {
  try {
    localStorage.setItem(
      'mipisto_last_crash',
      JSON.stringify({
        ...details,
        time: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      })
    );
  } catch (_) {
    // localStorage unavailable (private mode, quota, etc.) — nothing more we can do.
  }
}
